import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trocarSenha } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';

/**
 * Primeiro acesso com senha definida por outra pessoa: trocar é obrigatório antes de usar o sistema.
 *
 * A API recusa tudo fora do perfil (PASSWORD_CHANGE_REQUIRED), então a tela acompanha — mostrar o
 * app com todo clique falhando seria pior do que bloquear explicitamente.
 */
export function TrocaSenhaObrigatoria({ onSair }: { onSair: () => void }) {
  const { t } = useTranslation('auth');
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirma, setConfirma] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const submeter = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);

    if (nova.length < 8) return setErro(t('trocaObrigatoria.senhaCurta'));
    if (nova !== confirma) return setErro(t('trocaObrigatoria.naoConfere'));
    if (nova === atual) return setErro(t('trocaObrigatoria.igualAnterior'));

    setEnviando(true);
    try {
      await trocarSenha(atual, nova);
      // A troca revoga os refresh tokens no servidor: a sessão atual não vale mais, e entrar de
      // novo é o caminho mais previsível — o /me passa a vir sem a marca.
      toast.success(t('trocaObrigatoria.sucesso'));
      onSair();
    } catch {
      setErro(t('trocaObrigatoria.falha'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-4 rounded-lg border border-white/10 bg-white/5 p-6">
      <h1 className="font-display text-xl font-semibold">{t('trocaObrigatoria.titulo')}</h1>
      <p className="text-sm text-vyxora-text-light/70">{t('trocaObrigatoria.explicacao')}</p>

      <form onSubmit={submeter} className="space-y-3" noValidate>
        <label className="block">
          <span className="mb-1 block text-sm">{t('trocaObrigatoria.senhaAtual')}</span>
          <input
            type="password"
            className="input"
            autoComplete="current-password"
            value={atual}
            onChange={(e) => setAtual(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm">{t('trocaObrigatoria.novaSenha')}</span>
          <input
            type="password"
            className="input"
            autoComplete="new-password"
            value={nova}
            onChange={(e) => setNova(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm">{t('trocaObrigatoria.confirmar')}</span>
          <input
            type="password"
            className="input"
            autoComplete="new-password"
            value={confirma}
            onChange={(e) => setConfirma(e.target.value)}
          />
        </label>

        {erro && <p className="text-sm text-red-400">{erro}</p>}

        <div className="flex items-center justify-between gap-3 pt-1">
          <button type="button" onClick={onSair} className="text-sm text-vyxora-text-light/60 hover:text-vyxora-accent">
            {t('trocaObrigatoria.sair')}
          </button>
          <button type="submit" disabled={enviando} className="btn-primary text-sm">
            {enviando ? t('trocaObrigatoria.enviando') : t('trocaObrigatoria.confirmarTroca')}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Só quando a API marcou: o estado vem do banco, no /me, e não do token. */
export const precisaTrocarSenha = () => useAuthStore.getState().user?.deveTrocarSenha === true;
